<template>
  <div class="content-wrapper">
    <app-page-top-section :title="$t('client_dashboard')"></app-page-top-section>
    
    <div class="position-relative mt-4">
      <app-overlay-loader v-if="preloader" />
      
      <!-- Top 4 KPI Cards -->
      <div class="row mb-primary" :class="{ 'loading-opacity': preloader }">
        <div class="col-sm-6 col-lg-3 mb-4 mb-lg-0">
          <app-widget
            type="app-widget-with-icon"
            :label="$t('total_clients')"
            :number="integerFormatter(summaries.total_clients)"
            icon="users"
          />
        </div>

        <div class="col-sm-6 col-lg-3 mb-4 mb-lg-0">
          <app-widget
            type="app-widget-with-icon"
            :label="$t('total_projects')"
            :number="integerFormatter(summaries.total_projects)"
            icon="folder"
          />
        </div>

        <div class="col-sm-6 col-lg-3 mb-4 mb-sm-0">
          <app-widget
            type="app-widget-with-icon"
            :label="$t('total_payments')"
            :number="numberFormatter(summaries.total_payments)"
            icon="credit-card"
          />
        </div>

        <div class="col-sm-6 col-lg-3 mb-4 mb-sm-0">
          <app-widget
            type="app-widget-with-icon"
            :label="$t('total_due_amount')"
            :number="numberFormatter(summaries.total_due_amount)"
            icon="alert-circle"
          />
        </div>
      </div>

      <!-- Payment Collection Statistics Charts Row -->
      <div class="row mt-4 mb-primary" :class="{ 'loading-opacity': preloader }">
        <!-- Monthly Payments Chart -->
        <div class="col-md-6 mb-4 mb-md-0">
          <div class="card card-with-shadow border-0 h-100 min-height-350">
            <div class="card-header bg-transparent border-0 p-primary pb-0">
              <h5 class="card-title m-0">Monthly Payments Collection ({{ new Date().getFullYear() }})</h5>
            </div>
            <div class="card-body p-primary pt-2">
              <app-chart
                v-if="!preloader && monthlyDataSet[0].data.length"
                type="bar-chart"
                :height="300"
                :labels="monthlyLabels"
                :data-sets="monthlyDataSet"
              />
              <div v-else-if="!preloader" class="text-center p-primary text-muted min-height-300 d-flex align-items-center justify-content-center">
                No monthly collection records found.
              </div>
            </div>
          </div>
        </div>

        <!-- Yearly Payments Chart -->
        <div class="col-md-6">
          <div class="card card-with-shadow border-0 h-100 min-height-350">
            <div class="card-header bg-transparent border-0 p-primary pb-0">
              <h5 class="card-title m-0">Yearly Payments Collection</h5>
            </div>
            <div class="card-body p-primary pt-2">
              <app-chart
                v-if="!preloader && yearlyDataSet[0].data.length"
                type="bar-chart"
                :height="300"
                :labels="yearlyLabels"
                :data-sets="yearlyDataSet"
              />
              <div v-else-if="!preloader" class="text-center p-primary text-muted min-height-300 d-flex align-items-center justify-content-center">
                No yearly collection records found.
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Statistics Row -->
      <div class="row mt-4" :class="{ 'loading-opacity': preloader }">
        <!-- Recent Payments Table -->
        <div class="col-md-8 mb-4 mb-md-0">
          <div class="card card-with-shadow border-0 h-100">
            <div class="card-header bg-transparent border-0 p-primary">
              <h5 class="card-title m-0">Recent Payments</h5>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th class="border-top-0 pl-primary">Client</th>
                      <th class="border-top-0">Project</th>
                      <th class="border-top-0 text-right">Amount</th>
                      <th class="border-top-0 pr-primary">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="payment in summaries.recent_payments" :key="payment.id">
                      <td class="pl-primary">{{ payment.client_name }}</td>
                      <td>{{ payment.project_title }}</td>
                      <td class="text-right font-weight-bold">{{ numberFormatter(payment.amount) }}</td>
                      <td class="pr-primary">{{ payment.date }}</td>
                    </tr>
                    <tr v-if="!summaries.recent_payments || summaries.recent_payments.length === 0">
                      <td colspan="4" class="text-center p-primary text-muted">
                        No payments recorded yet.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Subscription Breakdown Panel -->
        <div class="col-md-4">
          <div class="card card-with-shadow border-0 h-100">
            <div class="card-header bg-transparent border-0 p-primary">
              <h5 class="card-title m-0">Subscription Types</h5>
            </div>
            <div class="card-body p-primary">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <span class="text-muted">Monthly Plans</span>
                <span class="badge bg-primary text-white p-2 font-weight-bold">{{ integerFormatter(summaries.subscription_breakdown ? summaries.subscription_breakdown.monthly : 0) }}</span>
              </div>
              <hr />
              <div class="d-flex justify-content-between align-items-center mb-3">
                <span class="text-muted">One-time / Fixed</span>
                <span class="badge bg-success text-white p-2 font-weight-bold">{{ integerFormatter(summaries.subscription_breakdown ? summaries.subscription_breakdown.one_time : 0) }}</span>
              </div>
              <hr />
              <div class="d-flex justify-content-between align-items-center mb-3">
                <span class="text-muted">Yearly Plans</span>
                <span class="badge bg-info text-white p-2 font-weight-bold">{{ integerFormatter(summaries.subscription_breakdown ? summaries.subscription_breakdown.yearly : 0) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { numberFormatter } from "../../../../common/Helper/Support/SettingsHelper";
import { axiosGet } from "../../../../common/Helper/AxiosHelper";
import { APP_DASHBOARD } from "../../../Config/ApiUrl";

export default {
  name: "ClientDashboard",
  data() {
    return {
      numberFormatter,
      preloader: false,
      summaries: {
        total_clients: 0,
        total_projects: 0,
        total_payments: "0.00",
        total_due_amount: "0.00",
        subscription_breakdown: {
          monthly: 0,
          one_time: 0,
          yearly: 0
        },
        recent_payments: []
      },
      monthlyLabels: [],
      monthlyDataSet: [
        {
          label: 'Collection',
          backgroundColor: '#019AFF',
          data: []
        }
      ],
      yearlyLabels: [],
      yearlyDataSet: [
        {
          label: 'Collection',
          backgroundColor: '#46c35f',
          data: []
        }
      ]
    };
  },
  created() {
    this.getClientDashboardData();
  },
  methods: {
    getClientDashboardData() {
      this.preloader = true;
      axiosGet(`${APP_DASHBOARD}/client-summary`).then(({ data }) => {
        this.summaries = data;
        
        // Bind monthly charts
        if (data.monthly_payments) {
          this.monthlyLabels = data.monthly_payments.labels;
          this.monthlyDataSet[0].data = data.monthly_payments.data;
          this.monthlyDataSet[0].backgroundColor = data.monthly_payments.data.map(() => '#019AFF');
        }
        
        // Bind yearly charts
        if (data.yearly_payments) {
          this.yearlyLabels = data.yearly_payments.labels;
          this.yearlyDataSet[0].data = data.yearly_payments.data;
          this.yearlyDataSet[0].backgroundColor = data.yearly_payments.data.map(() => '#46c35f');
        }

        this.preloader = false;
      }).catch(() => {
        this.preloader = false;
      });
    },
    integerFormatter(input) {
      if (input === null || input === undefined) return "0";
      let number = Number(input);
      if (isNaN(number)) return "0";
      return number.toString();
    }
  }
};
</script>

<style scoped>
.card-with-shadow {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
}
.p-primary {
  padding: 1.5rem;
}
.min-height-300 {
  min-height: 300px;
}
</style>
